import React from 'react';
import classnames from 'classnames';
import { Link, graphql } from 'gatsby';
import animateScrollTo from 'animated-scroll-to';
import { FiPrinter } from 'react-icons/fi';

import ExcessMortality from '../graphs/ExcessMortality';
import PooledByAgeGroup from '../graphs/PooledByAgeGroup';
import ZByCountry from '../graphs/ZByCountry';
import ZByCountryMap from '../graphs/ZByCountryMap';

import Layout from '../components/Layout';
import Meta from '../components/Meta';

import { win, getDocumentProp } from '../utils/dom';
import { getGraphsData } from '../utils/graphs';
import { usePrinting } from '../utils/hooks';

import grid from '../styles/grid.module.css';
import css from './graphs-and-maps.module.css';
import graphsJson from '../graphs/graphs.json';

const {
	ages,
	countries,
	reportYear,
	reportWeek,
	reportDay,
	pooledByAgeGroup,
	cumulatedExcessMortality,
	zByCountryMap,
	zByCountry,
} = getGraphsData(graphsJson);

const submenu = [
	{ id: 'pooled-by-age-group', label: ['Pooled number of deaths', 'by age group'] },
	{ id: 'excess-mortality', label: 'Excess mortality' },
	{ id: 'map-of-z-scores', label: 'Map of z-scores' },
	{ id: 'z-scores-by-country', label: 'Z-scores by country' },
];

const GraphsAndData = ({ navigate, location, data }) => {
	const { frontmatter, excerpt, html } = data.intro;
	const [printing, triggerPrint] = usePrinting();

	const allCountries = frontmatter.countries.split(',');

	const scrollToAnchor = async (e, id) => {
		if (!win) return;
		e.preventDefault();
		await animateScrollTo(document.querySelector(`#${id}`), { maxDuration: 1000 });
		navigate(`${location.pathname}#${id}`, {}, true);
	};

	const countryLabel =
		allCountries.length === countries.length
			? `all ${countries.length}`
			: `${countries.length} out of ${allCountries.length}`;

	return (
		<Layout title="Graphs and maps">
			<Meta title="Graphs and maps from EUROMOMO" description={excerpt} />
			<article className={classnames(grid.root, css.root)}>
				<header className={classnames(css.header, grid.root)}>
					<div className={classnames(css.title, grid.column6)}>
						<h1>Graphs and maps</h1>
						<p className={css.date}>
							Last updated on{' '}
							<time>
								week {reportWeek}, {reportYear}
							</time>
							<span className={css.print}>
								<a href="#" onClick={triggerPrint}>
									<FiPrinter />
									<span>Print</span>
								</a>
							</span>
						</p>
						<div className={css.intro} dangerouslySetInnerHTML={{ __html: html }} />

						<p className={css.participatingCountries}>
							These graphs were generated in week {reportYear}-{reportWeek} with data from {countryLabel}{' '}
							participating countries: {countries.join(', ')}.
						</p>
					</div>
					<div className={classnames(css.menu, grid.column2)}>
						<h3 className={css.title}>Graphs</h3>
						<ul>
							{submenu.map((item) => (
								<li key={item.id}>
									<Link
										to={`/graphs-and-maps/#${item.id}`}
										onClick={(e) => scrollToAnchor(e, item.id)}>
										{[].concat(item.label).map((l, i) => (
											<span key={i}>{l}</span>
										))}
									</Link>
								</li>
							))}
						</ul>
					</div>
				</header>
				<section className={grid.root}>
					<PooledByAgeGroup data={pooledByAgeGroup} ages={ages} printing={printing} />
					<ExcessMortality data={cumulatedExcessMortality} ages={ages} printing={printing} />
					<ZByCountryMap
						data={zByCountryMap}
						printing={printing}
						reportYear={reportYear}
						reportWeek={reportWeek}
					/>
					<ZByCountry data={zByCountry} ages={ages} printing={printing} />
				</section>
			</article>
		</Layout>
	);
};

export default GraphsAndData;

export const pageQuery = graphql`
	query($path: String!) {
		intro: markdownRemark(fields: { slug: { eq: $path } }) {
			frontmatter {
				title
				date
				countries
			}
			html
			excerpt
		}
	}
`;
